<template>
  <div class="HeaderBlock">
    <BaseImage
      v-if="!showVideoBackground"
      :media-object="content.imageObject"
      :sizes="{ md: 1920 }"
      class="hero-image"
    />

    <div
      v-else
      class="hero-image hero-video"
    >
      <UIYoutubePlayer
        v-if="isYoutube(videoUrl)"
        class="video-element"
        :video-id="extractYoutubeVideoId(videoUrl)"
        :show-controls="true"
        :muted="true"
        :autoplay="true"
        :loop="true"
      />

      <UIVimeoPlayer
        v-if="isVimeo(videoUrl)"
        class="video-element"
        :video-id="extractVimeoVideoId(videoUrl)"
        :show-controls="true"
        :muted="true"
        :autoplay="true"
        :loop="true"
      />
    </div>

    <div class="grid">
      <div class="inside col-7-12 sm-col-1-1">
        <UIHeadingType
          v-if="content.title"
          class="mb-0 title"
          :heading-type="content.seoHeadingtype"
          :title="content.title"
        />
        <div v-if="content.text1">
          {{ content.text1 }}
        </div>
        <div
          v-if="content.linkObject1?.name"
          class="btn-container"
        >
          <UIContentblockButton
            class="btn btn-primary"
            :name="content.linkObject1?.name"
            :link="content.linkObject1?.link"
            :target="content.linkObject1?.target"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import type { HeaderContent } from '@/types/contentblocks'
  import type { ElocusPage, BreadCrumb } from '@tc8l/nuxt-base'

  const props = defineProps<{
    content: HeaderContent
    breadCrumb?: BreadCrumb[]
    settings?: ElocusPage
    contentblockIdx?: number
  }>()

  const { lowStimulusActive, largeTextActive } = useBaseAccessibility()
  const { extractYoutubeVideoId, extractVimeoVideoId } = baseVideoUrlHelper()

  const videoUrl = computed(() => props.content.videoUrl ?? '')
  const showVideoBackground = computed(() => !!videoUrl.value && !lowStimulusActive.value && !largeTextActive.value)

  const isYoutube = (url: string) => {
    return url.includes('youtube') || url.includes('youtu.be')
  }

  const isVimeo = (url: string) => {
    return url.includes('vimeo')
  }
</script>

<style scoped lang="scss">
  .HeaderBlock {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: end;
    // flex: 1;
    padding: var(--padding-xxlg) 0 !important;
    height: 85svh;
    overflow: hidden;

    @include media(sm) {
      height: unset;
      min-height: 50rem;
    }
  }

  .hero-image {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      z-index: 1;
      pointer-events: none;
      background: linear-gradient(180deg, rgba(0, 0, 0, 0) 37.08%, rgba(0, 0, 0, 0.4) 100%);
    }

    :deep(.image-wrapper) {
      width: 100%;
      height: 100%;
      object-fit: cover;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }
  }

  .hero-video {
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    container-type: size;

    :deep(.video-element) {
      flex-shrink: 0;
      width: max(100%, calc(100cqh * 16 / 9));
      height: max(100%, calc(100cqw * 9 / 16));
    }
  }

  .grid {
    position: relative;
    z-index: 2;
    color: $white;
    pointer-events: none;
    height: var(--hero-media-height);
    display: flex;
    flex-direction: column;
    justify-content: end;
  }

  .inside {
    display: flex;
    flex-direction: column;
    gap: var(--margin-md);
  }

  .btn-container {
    :deep(.btn) {
      pointer-events: auto;
    }
  }

  .title {
    overflow-wrap: break-word;
    hyphens: auto;
    hyphenate-limit-chars: 16 4 4;
  }
</style>
